/*
  Filters and sort (docs/page-specs/collection.md "Grid and filters").

  <facet-filters> wraps the filter panel, the toolbar, the applied-filter chips
  and the grid of one section. Any change in the filter form fetches the same
  section through the Section Rendering API (?section_id=) and swaps the
  grid, the filter groups, the chips, the counts and the live result count in
  place; nothing is rebuilt in JavaScript (docs/08). The URL follows with
  pushState so the view is shareable and the back button works.

  Below 990px the panel is a bottom sheet: the "Filter and sort" button opens
  it with role="dialog", focus is trapped (global.js), Escape, the close
  button, the overlay and the "Show n results" button close it. On desktop
  the same panel is the sticky sidebar and none of that applies.

  <price-range> keeps the two number inputs and the two range thumbs of the
  price filter in step. Only the number inputs are submitted.
*/

class FacetFilters extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.resultsUrl = this.dataset.resultsUrl || window.location.pathname;
    this.form = this.querySelector('[data-facets-form]');
    this.panel = this.querySelector('[data-facets]');
    this.toggle = this.querySelector('[data-facets-toggle]');
    this.overlay = this.querySelector('[data-facets-overlay]');
    this.cache = new Map();
    this.lastParams = window.location.search.slice(1);
    this.desktop = window.matchMedia('(min-width: 990px)');

    this.debouncedRender = debounce((event) => this.onFormInput(event), 500);

    if (this.form) {
      this.form.addEventListener('input', (event) => {
        // Slider drags are committed by price-range on change, not per tick.
        if (event.target.matches('[data-price-slider]')) return;
        this.debouncedRender(event);
      });
      this.form.addEventListener('submit', (event) => this.onFormSubmit(event));
    }
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    if (this.toggle) this.toggle.addEventListener('click', () => this.openSheet());
    if (this.overlay) this.overlay.addEventListener('click', () => this.closeSheet());
    this.desktop.addEventListener('change', () => {
      if (this.desktop.matches) this.closeSheet(false);
    });

    window.addEventListener('popstate', () => {
      const params = window.location.search.slice(1);
      if (params === this.lastParams) return;
      this.render(params, { pushState: false, keepPage: true });
    });
  }

  get sheetOpen() {
    return this.panel?.classList.contains('facets--open');
  }

  // The form's state as query parameters, without empty values and without
  // the default sort, so the URL stays as Shopify itself would write it.
  formParams() {
    const params = new URLSearchParams(new FormData(this.form));
    [...params.keys()].forEach((key) => {
      if (params.getAll(key).every((value) => value === '')) params.delete(key);
    });
    if (params.get('sort_by') === this.form.dataset.defaultSort) params.delete('sort_by');
    return params.toString();
  }

  onFormInput(event) {
    this.render(this.formParams(), { trigger: event.target });
  }

  onFormSubmit(event) {
    event.preventDefault();
    const params = this.formParams();
    if (this.sheetOpen) this.closeSheet();
    if (params === this.lastParams) return;
    this.render(params);
  }

  onClick(event) {
    const remove = event.target.closest('[data-facet-remove]');
    if (remove) {
      event.preventDefault();
      const href = remove.getAttribute('href') || '';
      const index = href.indexOf('?');
      const params = index === -1 ? '' : href.slice(index + 1);
      this.render(params);
      return;
    }
    if (event.target.closest('[data-facets-close]')) this.closeSheet();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.sheetOpen) {
      event.preventDefault();
      this.closeSheet();
    }
  }

  openSheet() {
    if (!this.panel || this.desktop.matches) return;
    this.panel.classList.add('facets--open');
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('facets-sheet-open');
    trapFocus(this.panel, this.panel.querySelector('[data-facets-close]') || this.panel);
  }

  closeSheet(restoreFocus = true) {
    if (!this.panel) return;
    const wasOpen = this.sheetOpen;
    this.panel.classList.remove('facets--open');
    // Back to the named region it is on desktop (aside cannot carry role="dialog").
    this.panel.setAttribute('role', 'region');
    this.panel.removeAttribute('aria-modal');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('facets-sheet-open');
    if (wasOpen) removeTrapFocus(restoreFocus ? this.toggle : null);
  }

  render(rawParams, { pushState = true, trigger = null, keepPage = false } = {}) {
    // A filter or sort change starts again from page 1; only the back button
    // (popstate) re-renders a specific page.
    const params = keepPage ? rawParams : rawParams.replace(/(^|&)page=\d+/, '').replace(/^&/, '');
    // Section rendering: same path, same params, plus the section id.
    const url = `${this.resultsUrl}?${params}${params ? '&' : ''}section_id=${this.sectionId}`;
    this.lastParams = params;
    this.setLoading(true);

    const request = this.cache.get(url) || fetch(url).then((response) => response.text());
    this.cache.set(url, request);

    request
      .then((html) => {
        if (this.lastParams !== params) return;
        this.swap(new DOMParser().parseFromString(html, 'text/html'), trigger);
        if (pushState) {
          history.pushState({ facets: params }, '', `${this.resultsUrl}${params ? '?' : ''}${params}`);
        }
      })
      .catch((error) => {
        this.cache.delete(url);
        console.error(error);
      })
      .finally(() => this.setLoading(false));
  }

  setLoading(loading) {
    const grid = this.querySelector(`#ProductGridContainer-${this.sectionId}`);
    if (grid) {
      grid.classList.toggle('is-loading', loading);
      grid.setAttribute('aria-busy', String(loading));
    }
  }

  swap(doc, trigger) {
    const id = this.sectionId;
    const swapInner = (selector) => {
      const target = this.querySelector(selector);
      const source = doc.querySelector(selector);
      if (!target || !source) return;
      target.innerHTML = source.innerHTML;
      target.className = source.className;
    };

    swapInner(`#ProductGridContainer-${id}`);
    swapInner(`#ActiveFacets-${id}`);
    swapInner('[data-facets-count]');
    swapInner('[data-facets-toggle-count]');

    const count = this.querySelector(`#ProductCount-${id}`);
    const sourceCount = doc.querySelector(`#ProductCount-${id}`);
    if (count && sourceCount) {
      count.innerHTML = sourceCount.innerHTML;
      count.dataset.productCount = sourceCount.dataset.productCount || '';
      count.dataset.totalCount = sourceCount.dataset.totalCount || '';
    }

    const clear = this.querySelector('[data-facets-clear]');
    const sourceClear = doc.querySelector('[data-facets-clear]');
    if (clear && sourceClear) clear.className = sourceClear.className;

    const sort = this.querySelector(`#SortBy-${id}`);
    const sourceSort = doc.querySelector(`#SortBy-${id}`);
    if (sort && sourceSort && trigger !== sort) sort.value = sourceSort.value;

    this.swapGroups(doc, trigger);
  }

  swapGroups(doc, trigger) {
    const container = this.querySelector(`#FacetsGroups-${this.sectionId}`);
    const sourceContainer = doc.querySelector(`#FacetsGroups-${this.sectionId}`);
    if (!container || !sourceContainer) return;

    const triggerGroup = trigger ? trigger.closest('.js-filter') : null;
    const triggerIsPrice = Boolean(trigger && trigger.closest('price-range'));
    const sourceGroups = Array.from(sourceContainer.querySelectorAll('.js-filter'));
    const sourceIds = sourceGroups.map((group) => group.id);

    // Groups the server no longer returns.
    container.querySelectorAll('.js-filter').forEach((group) => {
      if (!sourceIds.includes(group.id)) group.remove();
    });

    let previous = null;
    sourceGroups.forEach((sourceGroup) => {
      const current = container.querySelector(`#${CSS.escape(sourceGroup.id)}`);
      if (current) {
        // Keep the shopper's open/closed state and, while they are typing a
        // price, their input; everything else takes the server's markup.
        const skip = triggerIsPrice && current === triggerGroup;
        if (!skip) current.innerHTML = sourceGroup.innerHTML;
        previous = current;
      } else {
        if (previous) previous.after(sourceGroup);
        else container.prepend(sourceGroup);
        previous = sourceGroup;
      }
    });

    // Put focus back on the checkbox that was just toggled.
    if (trigger && trigger.id && !triggerIsPrice) {
      const again = container.querySelector(`#${CSS.escape(trigger.id)}`);
      if (again) again.focus();
    }
  }
}

customElements.define('facet-filters', FacetFilters);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.min = this.querySelector('[data-price-input="min"]');
    this.max = this.querySelector('[data-price-input="max"]');
    this.minSlider = this.querySelector('[data-price-slider="min"]');
    this.maxSlider = this.querySelector('[data-price-slider="max"]');
    this.track = this.querySelector('[data-price-track]');
    this.rangeMax = Number(this.dataset.rangeMax) || 0;

    [this.min, this.max].forEach((input) => {
      input?.addEventListener('change', () => this.fromInputs());
    });
    [this.minSlider, this.maxSlider].forEach((slider) => {
      slider?.addEventListener('input', (event) => this.fromSliders(event.target));
      slider?.addEventListener('change', () => this.commit());
    });
    this.paint();
  }

  clamp(value, low, high) {
    return Math.min(Math.max(value, low), high);
  }

  fromInputs() {
    let low = this.min.value === '' ? 0 : Number(this.min.value);
    let high = this.max.value === '' ? this.rangeMax : Number(this.max.value);
    low = this.clamp(low, 0, this.rangeMax);
    high = this.clamp(high, 0, this.rangeMax);
    if (low > high) [low, high] = [high, low];
    if (this.min.value !== '') this.min.value = low;
    if (this.max.value !== '') this.max.value = high;
    this.minSlider.value = low;
    this.maxSlider.value = high;
    this.paint();
  }

  fromSliders(moved) {
    let low = Number(this.minSlider.value);
    let high = Number(this.maxSlider.value);
    if (low > high) {
      if (moved === this.minSlider) this.maxSlider.value = low;
      else this.minSlider.value = high;
      low = Number(this.minSlider.value);
      high = Number(this.maxSlider.value);
    }
    this.min.value = low > 0 ? low : '';
    this.max.value = high < this.rangeMax ? high : '';
    this.paint();
  }

  // A finished slider drag submits through the form's input listener.
  commit() {
    this.min.dispatchEvent(new Event('input', { bubbles: true }));
  }

  paint() {
    if (!this.track || !this.rangeMax) return;
    const low = (Number(this.minSlider.value) / this.rangeMax) * 100;
    const high = (Number(this.maxSlider.value) / this.rangeMax) * 100;
    this.track.style.setProperty('--range-low', `${low}%`);
    this.track.style.setProperty('--range-high', `${high}%`);
  }
}

customElements.define('price-range', PriceRange);
